/**
 * @fileoverview Timesheet firm configuration (MOR-906)
 *
 * Four sections, mirroring the settings-manager house style:
 * 1. Billing increment — the firm's unit length (6-minute default).
 * 2. Time categories — the general head categories (seeded insolvency set;
 *    system rows renameable, not removable) with IPS analysis-code mapping.
 * 3. Staff grades & rate cards — grades with effective-dated hourly rates;
 *    a rate rise adds a new dated row and never rewrites history.
 * 4. Staff — grade assignment and IPS staff-code overrides per member.
 *
 * Admin/owner only for writes — `canEdit` gates the UI; mutations enforce
 * the same rule server-side.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { format } from "date-fns";
import {
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Lock,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import { AU_JURISDICTIONS } from "@cvx/lib/businessDays";
import { weeklyHours, type WorkSchedule } from "@cvx/timesheets/workSchedule";
import { ColorSwatchPicker } from "@/components/settings/tasks/ColorSwatchPicker";
import { CategoryBadge } from "./CategoryBadge";
import { ApproverScopeControl } from "./ApproverScopeControl";
import { GradeRateTimeline } from "./GradeRateTimeline";
import { RateCardsSection } from "./RateCardsSection";
import { formatRate } from "./rateCards.helpers";
import { ScheduleRatesDialog } from "./ScheduleRatesDialog";
import { GROUP_CARD_CLASS } from "./settingsStyles";
import { DraftEstimatePolicySection } from "./DraftEstimatePolicySection";
import { UserRateOverridesDialog } from "./UserRateOverridesDialog";
import { WorkScheduleDialog } from "./WorkScheduleDialog";
import { WipImportDialog } from "./import/WipImportDialog";
import { WipImportReview } from "./import/WipImportReview";
import { Switch } from "@/ui/primitives/switch";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";

import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { Badge, Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Spinner } from "@/ui/primitives/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";

export function TimesheetSettingsManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const [importOpen, setImportOpen] = useState(false);
  const [reviewRunId, setReviewRunId] =
    useState<Id<"timesheetImportRuns"> | null>(null);

  return (
    <div className="flex w-full flex-col gap-6">
      {!canEdit && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 size-4 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Read-only view</p>
            <p className="mt-0.5 text-xs opacity-80">
              You need owner or admin access to change timesheet settings.
            </p>
          </div>
        </div>
      )}

      {reviewRunId ? (
        <WipImportReview
          runId={reviewRunId}
          orgId={orgId}
          onDone={() => setReviewRunId(null)}
        />
      ) : (
        <>
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-semibold tracking-tight">
              Timesheet Settings
            </h1>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportOpen(true)}
              >
                <Sparkles className="mr-1.5 size-4" />
                Import from spreadsheet
              </Button>
            )}
          </div>
          {/* Tabs group the firm configuration into one section per tab rather
              than one long scroll, mirroring the job Overview header. */}
          <Tabs defaultValue="all" className="flex min-w-0 flex-1 flex-col">
            <TabsList
              variant="underline"
              className="w-full shrink-0 flex-nowrap justify-start gap-3 overflow-x-auto [scrollbar-width:none]"
            >
              <TabsTrigger variant="underline" value="all" className="shrink-0">
                All
              </TabsTrigger>
              <TabsTrigger
                variant="underline"
                value="general"
                className="shrink-0"
              >
                General Settings
              </TabsTrigger>
              <TabsTrigger
                variant="underline"
                value="categories"
                className="shrink-0"
              >
                Categories
              </TabsTrigger>
              <TabsTrigger
                variant="underline"
                value="activities"
                className="shrink-0"
              >
                Activities
              </TabsTrigger>
              <TabsTrigger
                variant="underline"
                value="grades"
                className="shrink-0"
              >
                Grades &amp; Rates
              </TabsTrigger>
              <TabsTrigger
                variant="underline"
                value="staff"
                className="shrink-0"
              >
                Staff
              </TabsTrigger>
              <TabsTrigger
                variant="underline"
                value="narrative"
                className="shrink-0"
              >
                Narrative flagging
              </TabsTrigger>
            </TabsList>

            {/* "All" stacks every section vertically, so the full
                configuration is available on one screen (mirrors the job
                Overview tab). */}
            <TabsContent value="all" className="mt-5 flex flex-col gap-6">
              <UnitSection orgId={orgId} canEdit={canEdit} />
              <CategoriesSection orgId={orgId} canEdit={canEdit} />
              <FirmActivitiesSection orgId={orgId} canEdit={canEdit} />
              <GradesSection orgId={orgId} canEdit={canEdit} />
              <RateCardsSection
                orgId={orgId}
                canEdit={canEdit}
                today={format(new Date(), "yyyy-MM-dd")}
              />
              <StaffSection orgId={orgId} canEdit={canEdit} />
              <NarrativeContextSection orgId={orgId} canEdit={canEdit} />
              <DraftEstimatePolicySection orgId={orgId} canEdit={canEdit} />
            </TabsContent>
            <TabsContent value="general" className="mt-5 flex flex-col gap-6">
              <UnitSection orgId={orgId} canEdit={canEdit} />
            </TabsContent>
            <TabsContent
              value="categories"
              className="mt-5 flex flex-col gap-6"
            >
              <CategoriesSection orgId={orgId} canEdit={canEdit} />
            </TabsContent>
            <TabsContent
              value="activities"
              className="mt-5 flex flex-col gap-6"
            >
              <FirmActivitiesSection orgId={orgId} canEdit={canEdit} />
            </TabsContent>
            <TabsContent value="grades" className="mt-5 flex flex-col gap-6">
              <GradesSection orgId={orgId} canEdit={canEdit} />
              <RateCardsSection
                orgId={orgId}
                canEdit={canEdit}
                today={format(new Date(), "yyyy-MM-dd")}
              />
            </TabsContent>
            <TabsContent value="staff" className="mt-5 flex flex-col gap-6">
              <StaffSection orgId={orgId} canEdit={canEdit} />
            </TabsContent>
            <TabsContent value="narrative" className="mt-5 flex flex-col gap-6">
              <NarrativeContextSection orgId={orgId} canEdit={canEdit} />
            </TabsContent>
          </Tabs>
        </>
      )}

      <WipImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        orgId={orgId}
        onAnalysed={setReviewRunId}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Billing increment
// ---------------------------------------------------------------------------

function UnitSection({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: settings } = useQuery(
    convexQuery(api.timesheets.settings.getForOrg, { orgId }),
  );
  const setUnitMinutes = useMutation(api.timesheets.settings.setUnitMinutes);
  const setExpectedDailyUnits = useMutation(
    api.timesheets.settings.setExpectedDailyUnits,
  );
  const setIpsSubAnalysisCode = useMutation(
    api.timesheets.settings.setIpsSubAnalysisCode,
  );
  const setHolidayJurisdiction = useMutation(
    api.timesheets.settings.setHolidayJurisdiction,
  );
  const setRoundingMode = useMutation(api.timesheets.settings.setRoundingMode);
  const [expectedHours, setExpectedHours] = useState<string | null>(null);
  const [ipsCode, setIpsCode] = useState<string | null>(null);

  const handleChange = async (value: string) => {
    try {
      await setUnitMinutes({ orgId, unitMinutes: Number(value) });
      toast.success("Billing increment saved.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the setting."));
    }
  };

  const unitMinutes = settings?.unitMinutes ?? 6;
  const expectedHoursValue =
    expectedHours ??
    (settings ? String((settings.expectedDailyUnits * unitMinutes) / 60) : "");

  const commitExpectedHours = async () => {
    if (expectedHours === null || !settings) return;
    const hours = Number(expectedHours);
    if (!Number.isFinite(hours) || hours < 0 || hours > 24) {
      setExpectedHours(null);
      return;
    }
    try {
      await setExpectedDailyUnits({
        orgId,
        expectedDailyUnits: Math.round((hours * 60) / unitMinutes),
      });
      toast.success("Expected daily hours saved.");
      setExpectedHours(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the setting."));
    }
  };

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="border-b border-border/50 p-6">
        <h2 className="text-base font-semibold text-primary">
          General Settings
        </h2>
        <p className="mt-1 text-sm text-primary/60">
          Firm-wide defaults for time recording — billing increment, expected
          hours, public holidays and the IPS sub-analysis code.
        </p>
      </div>
      <div className="flex items-start justify-between gap-6 p-6">
        <div>
          <Label htmlFor="unit-minutes">Billing increment</Label>
          <p className="mt-1 text-sm text-muted-foreground">
            The length of one unit of time. Insolvency firms typically record in
            6-minute units (10 units per hour).
          </p>
        </div>
        <Select
          value={settings ? String(settings.unitMinutes) : undefined}
          onValueChange={(value) => void handleChange(value)}
          disabled={!canEdit || settings === undefined}
        >
          <SelectTrigger id="unit-minutes" className="w-40 shrink-0">
            <SelectValue placeholder="…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="6">6 minutes</SelectItem>
            <SelectItem value="10">10 minutes</SelectItem>
            <SelectItem value="15">15 minutes</SelectItem>
            <SelectItem value="30">30 minutes</SelectItem>
            <SelectItem value="60">60 minutes</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-start justify-between gap-6 border-t border-border/50 p-6">
        <div>
          <Label htmlFor="rounding-mode">Unit rounding</Label>
          <p className="mt-1 text-sm text-muted-foreground">
            How a part unit is turned into a whole billing unit — when recording
            a duration or typing a fractional figure. Any time recorded is still
            at least one unit.
          </p>
        </div>
        <Select
          value={settings?.roundingMode}
          onValueChange={(value) =>
            setRoundingMode({
              orgId,
              roundingMode:
                value === "nearest"
                  ? "nearest"
                  : value === "down"
                    ? "down"
                    : "up",
            })
              .then(() => toast.success("Rounding rule saved."))
              .catch((err: unknown) =>
                toast.error(
                  extractErrorMessage(err, "Could not save the setting."),
                ),
              )
          }
          disabled={!canEdit || settings === undefined}
        >
          <SelectTrigger id="rounding-mode" className="w-40 shrink-0">
            <SelectValue placeholder="…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="up">Round up</SelectItem>
            <SelectItem value="nearest">Nearest</SelectItem>
            <SelectItem value="down">Round down</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-start justify-between gap-6 border-t border-border/50 p-6">
        <div>
          <Label htmlFor="expected-daily-hours">Expected daily hours</Label>
          <p className="mt-1 text-sm text-muted-foreground">
            The firm's baseline per working day, Monday to Friday. Anyone
            without their own work schedule (set per person under Staff) is
            measured against this for the daily balance, the missing timesheets
            report and utilisation analytics.
          </p>
        </div>
        <Input
          id="expected-daily-hours"
          type="number"
          min={0}
          max={24}
          step={0.5}
          value={expectedHoursValue}
          onChange={(e) => setExpectedHours(e.target.value)}
          onBlur={() => void commitExpectedHours()}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          disabled={!canEdit || settings === undefined}
          className="w-40 shrink-0"
        />
      </div>
      <div className="flex items-start justify-between gap-6 border-t border-border/50 p-6">
        <div>
          <Label htmlFor="holiday-jurisdiction">Public holidays</Label>
          <p className="mt-1 text-sm text-muted-foreground">
            The state or territory whose public holidays count as days off, so
            nobody is chased for time on them. National only if unset.
          </p>
        </div>
        <Select
          value={
            settings ? settings.holidayJurisdiction || "national" : undefined
          }
          onValueChange={(value) =>
            setHolidayJurisdiction({
              orgId,
              jurisdiction: value === "national" ? "" : value,
            })
              .then(() => toast.success("Public holiday calendar saved."))
              .catch((err: unknown) =>
                toast.error(
                  extractErrorMessage(err, "Could not save the setting."),
                ),
              )
          }
          disabled={!canEdit || settings === undefined}
        >
          <SelectTrigger id="holiday-jurisdiction" className="w-40 shrink-0">
            <SelectValue placeholder="…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="national">National only</SelectItem>
            {AU_JURISDICTIONS.map((j) => (
              <SelectItem key={j} value={j}>
                {j}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-start justify-between gap-6 border-t border-border/50 p-6">
        <div>
          <Label htmlFor="ips-sub-analysis">IPS sub-analysis code</Label>
          <p className="mt-1 text-sm text-muted-foreground">
            The ceremony field auto-filled on every IPS export row — IPS
            requires it, remuneration reports ignore it.
          </p>
        </div>
        <Input
          id="ips-sub-analysis"
          value={ipsCode ?? settings?.ipsSubAnalysisCode ?? ""}
          onChange={(e) => setIpsCode(e.target.value.toUpperCase())}
          onBlur={() => {
            if (ipsCode === null || !settings) return;
            if (ipsCode.trim() === settings.ipsSubAnalysisCode) {
              setIpsCode(null);
              return;
            }
            setIpsSubAnalysisCode({ orgId, code: ipsCode })
              .then(() => {
                toast.success("Sub-analysis code saved.");
                setIpsCode(null);
              })
              .catch((err: unknown) =>
                toast.error(
                  extractErrorMessage(err, "Could not save the setting."),
                ),
              );
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          disabled={!canEdit || settings === undefined}
          className="w-40 shrink-0 font-mono uppercase"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1b. Narrative flagging & firm context (MOR-1039)
// ---------------------------------------------------------------------------

/**
 * The firm's narrative-flagging context. Off by default: a soft prompt only
 * runs once the firm switches it on, and it is judged against the firm's own
 * guidance (their ARITA Code references and internal policies) rather than any
 * wording we cannot vouch for. The heuristic that decides *whether* a
 * narrative is thin is unchanged; this only controls whether it runs and what
 * reference text it shows.
 */
function NarrativeContextSection({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: settings } = useQuery(
    convexQuery(api.timesheets.settings.getForOrg, { orgId }),
  );
  const setNarrativeGuidance = useMutation(
    api.timesheets.settings.setNarrativeGuidance,
  );
  // The textarea is a draft until saved; `null` means "show the stored value".
  const [contextDraft, setContextDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const enabled = settings?.narrativeFlaggingEnabled ?? false;
  const contextValue = contextDraft ?? settings?.narrativeContext ?? "";
  const dirty =
    contextDraft !== null &&
    contextDraft !== (settings?.narrativeContext ?? "");

  const persist = async (next: { enabled: boolean; context: string }) => {
    setSaving(true);
    try {
      await setNarrativeGuidance({ orgId, ...next });
      toast.success("Narrative guidance saved.");
      setContextDraft(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the setting."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="flex items-start justify-between gap-6 border-b border-border/50 p-6">
        <div>
          <h2 className="text-lg font-semibold text-primary">
            Narrative flagging
          </h2>
          <p className="mt-1 text-sm text-primary/60">
            Generates prompts around narrative in accordance with context and
            policies added by your firm.
          </p>
        </div>
        <Switch
          id="narrative-flagging"
          aria-label="Flag thin narratives"
          checked={enabled}
          onCheckedChange={(checked) =>
            void persist({ enabled: checked, context: contextValue })
          }
          disabled={!canEdit || settings === undefined || saving}
        />
      </div>

      {/* The firm context only matters once flagging is on, so it stays hidden
          until the toggle is switched on (MOR-1039). */}
      {enabled && (
        <div className="flex flex-col gap-3 border-t border-border/50 p-6">
          <div>
            <Label htmlFor="narrative-context">
              Firm context &amp; policies
            </Label>
            <p className="mt-1 text-sm text-muted-foreground">
              Paste guidance for narrative flagging (e.g. ARITA Practice
              Statement or internal policies). If left blank, narrative flagging
              will be based on generic recommendations.
            </p>
          </div>
          <Textarea
            id="narrative-context"
            value={contextValue}
            onChange={(e) => setContextDraft(e.target.value)}
            rows={6}
            placeholder="e.g. ARITA Code of Professional Practice, s 15.1 (Recording of Work Done): keep a record of the details of the work performed, specific to this appointment…"
            disabled={!canEdit || settings === undefined || saving}
            className="text-sm"
          />
          <div className="flex justify-end gap-2">
            {dirty && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setContextDraft(null)}
                disabled={saving}
              >
                Cancel
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => void persist({ enabled, context: contextValue })}
              disabled={!canEdit || settings === undefined || saving || !dirty}
            >
              Save guidance
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Time categories
// ---------------------------------------------------------------------------

function CategoriesSection({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: categories } = useQuery(
    convexQuery(api.timesheets.categories.listByOrg, { orgId }),
  );
  const createCategory = useMutation(api.timesheets.categories.create);
  const updateCategory = useMutation(api.timesheets.categories.update);
  const archiveCategory = useMutation(api.timesheets.categories.archive);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editIpsCode, setEditIpsCode] = useState("");
  const [editColor, setEditColor] = useState<TaskColor>("gray");
  const [editSubCodes, setEditSubCodes] = useState<SubCode[]>([]);
  const [newName, setNewName] = useState("");
  const [newIpsCode, setNewIpsCode] = useState("");

  const startEdit = (c: {
    _id: Id<"timeCategories">;
    name: string;
    ipsCode?: string;
    color?: TaskColor;
    icon?: string;
    subCodes: SubCode[];
  }) => {
    setEditingId(c._id as string);
    setEditName(c.name);
    setEditIpsCode(c.ipsCode ?? "");
    setEditColor(c.color ?? "gray");
    setEditSubCodes(c.subCodes);
  };

  const saveEdit = async (categoryId: Id<"timeCategories">) => {
    try {
      await updateCategory({
        categoryId,
        patch: {
          name: editName,
          ipsCode: editIpsCode.trim() || null,
          color: editColor,
          subCodes: editSubCodes,
        },
      });
      setEditingId(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not update the category."));
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createCategory({
        orgId,
        name: newName.trim(),
        ipsCode: newIpsCode.trim() || undefined,
      });
      setNewName("");
      setNewIpsCode("");
      toast.success("Category added.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not add the category."));
    }
  };

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="border-b border-border/50 p-6">
        <h2 className="text-base font-semibold text-primary">
          Time Categories
        </h2>
        <p className="mt-1 text-sm text-primary/60">
          General head categories recorded against every entry.
        </p>
      </div>
      <div className="flex flex-col divide-y divide-border/50">
        {(categories ?? [])
          .filter((c) => c.scope !== "firm")
          .map((c) => (
            <div key={c._id} className="flex items-center gap-3 px-6 py-3">
              {editingId === (c._id as string) ? (
                <div className="flex w-full flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-8 max-w-56"
                    />
                    <Input
                      value={editIpsCode}
                      onChange={(e) => setEditIpsCode(e.target.value)}
                      placeholder="IPS/Insol Code"
                      className="h-8 max-w-36"
                    />
                    <Button
                      size="sm"
                      className="h-8"
                      onClick={() => void saveEdit(c._id)}
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={() => setEditingId(null)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <ColorSwatchPicker
                      value={editColor}
                      onChange={setEditColor}
                    />
                  </div>
                  <SubCodeEditor
                    subCodes={editSubCodes}
                    onChange={setEditSubCodes}
                  />
                </div>
              ) : (
                <>
                  <CategoryBadge
                    category={{
                      name: c.name,
                      color: c.color,
                      icon: c.icon,
                      ipsCode: c.ipsCode,
                    }}
                    size="md"
                  />
                  <span className="text-xs text-muted-foreground">
                    {c.ipsCode ?? "No IPS/Insol code"}
                  </span>
                  {c.subCodes.length > 0 && (
                    <span
                      className="text-xs text-muted-foreground"
                      title={c.subCodes.map((s) => s.code).join(", ")}
                    >
                      {c.subCodes.length} sub-code
                      {c.subCodes.length === 1 ? "" : "s"}
                    </span>
                  )}
                  {c.isSystem && (
                    <Badge className="bg-muted text-muted-foreground">
                      Default
                    </Badge>
                  )}
                  {canEdit && (
                    <div className="ml-auto flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        onClick={() => startEdit(c)}
                        aria-label={`Edit ${c.name}`}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      {!c.isSystem && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            archiveCategory({ categoryId: c._id }).catch(
                              (err: unknown) =>
                                toast.error(
                                  extractErrorMessage(
                                    err,
                                    "Could not archive the category.",
                                  ),
                                ),
                            )
                          }
                          aria-label={`Archive ${c.name}`}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        {canEdit && (
          <div className="flex items-center gap-3 px-6 py-3">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New category name"
              className="h-8 max-w-56"
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleCreate();
              }}
            />
            <Input
              value={newIpsCode}
              onChange={(e) => setNewIpsCode(e.target.value)}
              placeholder="IPS/Insol Code"
              className="h-8 max-w-36"
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleCreate();
              }}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              disabled={!newName.trim()}
              onClick={() => void handleCreate()}
            >
              <Plus className="size-4" />
              Add category
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2b. Firm activities (non-chargeable overhead)
// ---------------------------------------------------------------------------

/**
 * Firm activities are firm-scoped time categories (marketing, admin, training)
 * for work that must be recorded but is never billed to a matter. Time logged
 * against them still counts toward a person's expected hours, so it clears the
 * missing-timesheet chase, but it never carries an IPS code, sub-codes, a
 * matter or a rate. Rows are created with `scope: "firm"` and reuse the same
 * category CRUD as head categories.
 */
function FirmActivitiesSection({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: categories } = useQuery(
    convexQuery(api.timesheets.categories.listByOrg, { orgId }),
  );
  const createCategory = useMutation(api.timesheets.categories.create);
  const updateCategory = useMutation(api.timesheets.categories.update);
  const archiveCategory = useMutation(api.timesheets.categories.archive);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState<TaskColor>("gray");
  const [newName, setNewName] = useState("");

  const firmActivities = useMemo(
    () => (categories ?? []).filter((c) => c.scope === "firm"),
    [categories],
  );

  const startEdit = (c: {
    _id: Id<"timeCategories">;
    name: string;
    color?: TaskColor;
  }) => {
    setEditingId(c._id as string);
    setEditName(c.name);
    setEditColor(c.color ?? "gray");
  };

  const saveEdit = async (categoryId: Id<"timeCategories">) => {
    try {
      await updateCategory({
        categoryId,
        patch: { name: editName, color: editColor },
      });
      setEditingId(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not update the activity."));
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createCategory({
        orgId,
        name: newName.trim(),
        scope: "firm",
      });
      setNewName("");
      toast.success("Firm activity added.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not add the activity."));
    }
  };

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="border-b border-border/50 p-6">
        <h2 className="text-base font-semibold text-primary">
          Firm Activities
        </h2>
        <p className="mt-1 text-sm text-primary/60">
          Non-chargeable firm work — marketing, administration, training. Time
          logged here counts toward expected hours but is never billed to a
          matter.
        </p>
      </div>
      <div className="flex flex-col divide-y divide-border/50">
        {firmActivities.map((c) => (
          <div key={c._id} className="flex items-center gap-3 px-6 py-3">
            {editingId === (c._id as string) ? (
              <div className="flex w-full flex-col gap-2">
                <div className="flex items-center gap-3">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-8 max-w-56"
                  />
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={() => void saveEdit(c._id)}
                  >
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => setEditingId(null)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <ColorSwatchPicker
                    value={editColor}
                    onChange={setEditColor}
                  />
                </div>
              </div>
            ) : (
              <>
                <CategoryBadge
                  category={{ name: c.name, color: c.color, icon: c.icon }}
                  size="md"
                />
                {c.isSystem && (
                  <Badge className="bg-muted text-muted-foreground">
                    Default
                  </Badge>
                )}
                {canEdit && (
                  <div className="ml-auto flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      onClick={() => startEdit(c)}
                      aria-label={`Edit ${c.name}`}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    {!c.isSystem && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          archiveCategory({ categoryId: c._id }).catch(
                            (err: unknown) =>
                              toast.error(
                                extractErrorMessage(
                                  err,
                                  "Could not archive the activity.",
                                ),
                              ),
                          )
                        }
                        aria-label={`Archive ${c.name}`}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {canEdit && (
          <div className="flex items-center gap-3 px-6 py-3">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New activity name (e.g. Marketing)"
              className="h-8 max-w-56"
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleCreate();
              }}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              disabled={!newName.trim()}
              onClick={() => void handleCreate()}
            >
              <Plus className="size-4" />
              Add activity
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

type SubCode = { code: string; name?: string };

/**
 * Sub-code list editor for one category, shown while the category is in edit
 * mode. Edits are held by the parent and written with the rest of the category
 * on Save, so a half-finished list is never persisted.
 */
function SubCodeEditor({
  subCodes,
  onChange,
}: {
  subCodes: SubCode[];
  onChange: (next: SubCode[]) => void;
}) {
  const [draftCode, setDraftCode] = useState("");
  const [draftName, setDraftName] = useState("");

  const add = () => {
    const code = draftCode.trim().toUpperCase();
    if (!code) return;
    if (subCodes.some((s) => s.code === code)) {
      toast.error(`"${code}" is already on this category.`);
      return;
    }
    onChange([...subCodes, { code, name: draftName.trim() || undefined }]);
    setDraftCode("");
    setDraftName("");
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-muted/30 p-3">
      <p className="text-xs text-muted-foreground">
        Accounting sub-codes for this category. Optional — leave empty if your
        firm does not use them.
      </p>
      {subCodes.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {subCodes.map((s) => (
            <span
              key={s.code}
              className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs"
            >
              <span className="font-medium">{s.code}</span>
              {s.name && (
                <span className="text-muted-foreground">{s.name}</span>
              )}
              <button
                type="button"
                onClick={() =>
                  onChange(subCodes.filter((x) => x.code !== s.code))
                }
                className="text-muted-foreground hover:text-destructive"
                aria-label={`Remove sub-code ${s.code}`}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          value={draftCode}
          onChange={(e) => setDraftCode(e.target.value)}
          placeholder="Sub-code"
          className="h-7 max-w-28 text-xs uppercase"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Input
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          placeholder="Label (optional)"
          className="h-7 max-w-48 text-xs"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          disabled={!draftCode.trim()}
          onClick={add}
        >
          <Plus className="size-3.5" />
          Add sub-code
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Staff grades & rate cards
// ---------------------------------------------------------------------------

function GradesSection({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const today = format(new Date(), "yyyy-MM-dd");
  const { data: grades } = useQuery(
    convexQuery(api.timesheets.grades.listByOrg, { orgId, today }),
  );
  const createGrade = useMutation(api.timesheets.grades.create);
  const updateGrade = useMutation(api.timesheets.grades.update);
  const archiveGrade = useMutation(api.timesheets.grades.archive);
  const addRate = useMutation(api.timesheets.grades.addRate);
  const deleteRate = useMutation(api.timesheets.grades.deleteRate);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [newName, setNewName] = useState("");
  const [newRate, setNewRate] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // Per-grade new-rate form state, keyed by grade id.
  const [rateDrafts, setRateDrafts] = useState<
    Record<string, { rate: string; from: string }>
  >({});

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createGrade({
        orgId,
        name: newName.trim(),
        hourlyRate: newRate ? Number(newRate) : undefined,
        effectiveFrom: newRate ? today : undefined,
      });
      setNewName("");
      setNewRate("");
      toast.success("Grade added.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not add the grade."));
    }
  };

  const handleAddRate = async (gradeId: Id<"staffGrades">) => {
    const draft = rateDrafts[gradeId as string];
    if (!draft?.rate || !draft.from) return;
    try {
      await addRate({
        gradeId,
        hourlyRate: Number(draft.rate),
        effectiveFrom: draft.from,
      });
      setRateDrafts((prev) => ({
        ...prev,
        [gradeId as string]: { rate: "", from: "" },
      }));
      toast.success("Rate added.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not add the rate."));
    }
  };

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="flex items-start justify-between gap-4 border-b border-border/50 p-6">
        <div>
          <h2 className="text-base font-semibold text-primary">
            Staff Grades &amp; Standard Rates
          </h2>
          <p className="mt-1 text-sm text-primary/60">
            Grades drive the hourly rate on every entry. Each rate applies from
            its date until the next one starts, so a rate rise never rewrites
            time already recorded. Open a grade to see which rate applies when.
          </p>
        </div>
        {canEdit && (grades ?? []).length > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 shrink-0"
            onClick={() => setScheduleOpen(true)}
          >
            <CalendarClock className="size-4" />
            Set new rates from a date
          </Button>
        )}
      </div>
      <div className="flex flex-col divide-y divide-border/50">
        {(grades ?? []).map((g) => {
          const isExpanded = expanded.has(g._id as string);
          const draft = rateDrafts[g._id as string] ?? { rate: "", from: "" };
          return (
            <div key={g._id} className="flex flex-col">
              <div className="flex items-center gap-3 px-6 py-3">
                <button
                  type="button"
                  onClick={() => toggleExpanded(g._id as string)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={
                    isExpanded ? "Hide rate history" : "Show rate history"
                  }
                >
                  {isExpanded ? (
                    <ChevronDown className="size-4" />
                  ) : (
                    <ChevronRight className="size-4" />
                  )}
                </button>
                {editingId === (g._id as string) ? (
                  <>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-8 max-w-56"
                    />
                    <Button
                      size="sm"
                      className="h-8"
                      onClick={() => {
                        updateGrade({
                          gradeId: g._id,
                          patch: { name: editName },
                        })
                          .then(() => setEditingId(null))
                          .catch((err: unknown) =>
                            toast.error(
                              extractErrorMessage(
                                err,
                                "Could not rename the grade.",
                              ),
                            ),
                          );
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={() => setEditingId(null)}
                    >
                      <X className="size-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="min-w-40 text-sm font-medium">
                      {g.name}
                    </span>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {g.currentRate !== undefined
                        ? formatRate(g.currentRate)
                        : "No current rate"}
                    </span>
                    {canEdit && (
                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7"
                          onClick={() => {
                            setEditingId(g._id as string);
                            setEditName(g.name);
                          }}
                          aria-label={`Rename ${g.name}`}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            archiveGrade({ gradeId: g._id }).catch(
                              (err: unknown) =>
                                toast.error(
                                  extractErrorMessage(
                                    err,
                                    "Could not archive the grade.",
                                  ),
                                ),
                            )
                          }
                          aria-label={`Archive ${g.name}`}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {isExpanded && (
                <div className="pl-13 flex flex-col gap-2 bg-muted/30 px-6 py-3">
                  <GradeRateTimeline
                    rates={g.rates}
                    today={today}
                    canEdit={canEdit}
                    onDelete={(rateId) =>
                      void deleteRate({ rateId }).catch((err: unknown) =>
                        toast.error(
                          extractErrorMessage(
                            err,
                            "Could not delete the rate.",
                          ),
                        ),
                      )
                    }
                  />
                  {canEdit && (
                    <div className="mt-1 flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        value={draft.rate}
                        onChange={(e) =>
                          setRateDrafts((prev) => ({
                            ...prev,
                            [g._id as string]: {
                              ...draft,
                              rate: e.target.value,
                            },
                          }))
                        }
                        placeholder="Hourly rate"
                        className="h-8 w-32"
                      />
                      <DatePicker
                        value={draft.from}
                        onChange={(isoDate) =>
                          setRateDrafts((prev) => ({
                            ...prev,
                            [g._id as string]: {
                              ...draft,
                              from: isoDate ?? "",
                            },
                          }))
                        }
                        placeholder="Effective from"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        disabled={!draft.rate || !draft.from}
                        onClick={() => void handleAddRate(g._id)}
                      >
                        <Plus className="size-4" />
                        Add rate
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {canEdit && (
          <div className="flex items-center gap-3 px-6 py-3">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New grade name (e.g. Senior Analyst)"
              className="h-8 max-w-64"
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleCreate();
              }}
            />
            <Input
              type="number"
              min={0}
              value={newRate}
              onChange={(e) => setNewRate(e.target.value)}
              placeholder="Hourly rate (optional)"
              className="h-8 max-w-44"
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleCreate();
              }}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              disabled={!newName.trim()}
              onClick={() => void handleCreate()}
            >
              <Plus className="size-4" />
              Add grade
            </Button>
          </div>
        )}
      </div>

      <ScheduleRatesDialog
        open={scheduleOpen}
        grades={grades ?? []}
        today={today}
        onOpenChange={setScheduleOpen}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Staff — grade assignment + staff codes
// ---------------------------------------------------------------------------

function StaffSection({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const today = format(new Date(), "yyyy-MM-dd");
  const { data: staff } = useQuery(
    convexQuery(api.timesheets.profiles.listOrgStaff, { orgId }),
  );
  const { data: grades } = useQuery(
    convexQuery(api.timesheets.grades.listByOrg, { orgId, today }),
  );
  const setGrade = useMutation(api.timesheets.profiles.setGrade);
  const setStaffCode = useMutation(api.timesheets.profiles.setStaffCode);
  const setIsApprover = useMutation(api.timesheets.profiles.setIsApprover);
  const setApproverTeams = useMutation(
    api.timesheets.profiles.setApproverTeams,
  );
  const { data: teams } = useQuery(
    convexQuery(api.organizations.teams.listTeams, { orgId }),
  );
  // Individual rates are admin material: the query refuses everyone else, so
  // it is only asked for when the viewer can edit.
  const { data: usersWithOverrides } = useQuery({
    ...convexQuery(api.timesheets.rateOverrides.usersWithOverrides, { orgId }),
    enabled: canEdit,
  });
  const [overridesFor, setOverridesFor] = useState<{
    userId: Id<"users">;
    name: string;
    gradeId?: Id<"staffGrades">;
  } | null>(null);
  // MOR-1458: the firm figure is the starting point for a new schedule.
  const { data: settings } = useQuery(
    convexQuery(api.timesheets.settings.getForOrg, { orgId }),
  );
  const [scheduleFor, setScheduleFor] = useState<{
    userId: Id<"users">;
    name: string;
    workSchedule?: WorkSchedule;
  } | null>(null);

  const gradeOptions = useMemo(() => grades ?? [], [grades]);
  const approverCount = (staff ?? []).filter((m) => m.isApprover).length;
  const uncoveredCount = (staff ?? []).filter((m) => !m.covered).length;

  if (staff === undefined) {
    return (
      <div
        className={cn(
          GROUP_CARD_CLASS,
          "flex h-40 items-center justify-center",
        )}
      >
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="border-b border-border/50 p-6">
        <h2 className="text-base font-semibold text-primary">Staff</h2>
        <p className="mt-1 text-sm text-primary/60">
          Assign each team member a grade (drives their rate), set their work
          schedule (expected hours per day) and select who approves timesheets.
          Approvers can review, adjust, approve, reject and write off timesheet
          entries. Approvers can be marked as an approver for the entire firm or
          for specific teams.
        </p>
        {approverCount === 0 && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
            No Approvers have been designated yet. Admins and Owners can approve
            timesheets until at least one Approver has been set.
          </p>
        )}
        {uncoveredCount > 0 && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
            {uncoveredCount === 1
              ? "1 person is not covered by any approver"
              : `${uncoveredCount} people are not covered by any approver`}{" "}
            (marked below). Their time goes to organisation admins and owners
            until you put them on an approved team or add an approver who
            approves everyone.
          </p>
        )}
      </div>
      <div className="flex flex-col divide-y divide-border/50">
        {staff.map((member) => (
          <StaffRow
            key={member.userId}
            member={member}
            gradeOptions={gradeOptions}
            canEdit={canEdit}
            teams={(teams ?? []).map((team) => ({
              _id: team._id,
              name: team.name,
            }))}
            onApproverTeamsChange={(teamIds) =>
              setApproverTeams({
                orgId,
                userId: member.userId,
                teamIds,
              }).catch((err: unknown) =>
                toast.error(
                  extractErrorMessage(
                    err,
                    "Could not update who they approve.",
                  ),
                ),
              )
            }
            hasIndividualRate={
              usersWithOverrides?.includes(member.userId) ?? false
            }
            onEditIndividualRate={() => setOverridesFor(member)}
            onEditSchedule={() => setScheduleFor(member)}
            onGradeChange={(gradeId) =>
              setGrade({
                orgId,
                userId: member.userId,
                gradeId:
                  gradeId === "none" ? null : (gradeId as Id<"staffGrades">),
              }).catch((err: unknown) =>
                toast.error(
                  extractErrorMessage(err, "Could not set the grade."),
                ),
              )
            }
            onStaffCodeCommit={(code) =>
              setStaffCode({
                orgId,
                userId: member.userId,
                staffCode: code,
              }).catch((err: unknown) =>
                toast.error(
                  extractErrorMessage(err, "Could not save the staff code."),
                ),
              )
            }
            onApproverChange={(isApprover) =>
              setIsApprover({ orgId, userId: member.userId, isApprover }).catch(
                (err: unknown) =>
                  toast.error(
                    extractErrorMessage(
                      err,
                      "Could not update the approver role.",
                    ),
                  ),
              )
            }
          />
        ))}
      </div>

      <UserRateOverridesDialog
        open={overridesFor !== null}
        orgId={orgId}
        member={overridesFor}
        gradeLabel={gradeLabelFor(gradeOptions, overridesFor?.gradeId)}
        today={today}
        onOpenChange={(open) => !open && setOverridesFor(null)}
      />
      <WorkScheduleDialog
        open={scheduleFor !== null}
        orgId={orgId}
        member={scheduleFor}
        firmDailyHours={
          settings
            ? (settings.expectedDailyUnits * settings.unitMinutes) / 60
            : 7.5
        }
        onOpenChange={(open) => !open && setScheduleFor(null)}
      />
    </div>
  );
}

/** "Partner, $650/hr" — what a person is charged at without an individual rate. */
function gradeLabelFor(
  grades: { _id: Id<"staffGrades">; name: string; currentRate?: number }[],
  gradeId: Id<"staffGrades"> | undefined,
): string {
  const grade = grades.find((g) => g._id === gradeId);
  if (!grade) return "no grade assigned";
  return grade.currentRate !== undefined
    ? `${grade.name}, ${formatRate(grade.currentRate)}`
    : `${grade.name}, no current rate`;
}

function StaffRow({
  member,
  gradeOptions,
  canEdit,
  teams,
  onApproverTeamsChange,
  hasIndividualRate,
  onEditIndividualRate,
  onEditSchedule,
  onGradeChange,
  onStaffCodeCommit,
  onApproverChange,
}: {
  member: {
    userId: Id<"users">;
    name: string;
    email: string;
    gradeId?: Id<"staffGrades">;
    staffCode: string;
    staffCodeIsDerived: boolean;
    isApprover: boolean;
    approverTeamIds?: Id<"teams">[];
    teamIds: Id<"teams">[];
    covered: boolean;
    workSchedule?: WorkSchedule;
  };
  gradeOptions: { _id: Id<"staffGrades">; name: string }[];
  canEdit: boolean;
  teams: { _id: Id<"teams">; name: string }[];
  onApproverTeamsChange: (teamIds: Id<"teams">[] | null) => void;
  /** Whether this person has any individual rate on record. */
  hasIndividualRate: boolean;
  onEditIndividualRate: () => void;
  /** MOR-1458: open the work schedule dialog for this person. */
  onEditSchedule: () => void;
  onGradeChange: (gradeId: string) => void;
  onStaffCodeCommit: (code: string) => void;
  onApproverChange: (isApprover: boolean) => void;
}) {
  const [code, setCode] = useState(member.staffCode);

  return (
    <div className="flex flex-wrap items-center gap-3 px-6 py-3">
      <div className="flex min-w-52 flex-col">
        <span className="text-sm font-medium">{member.name}</span>
        <span className="text-xs text-muted-foreground">{member.email}</span>
        <span className="text-xs text-muted-foreground">
          {member.teamIds.length > 0
            ? teams
                .filter((team) => member.teamIds.includes(team._id))
                .map((team) => team.name)
                .join(", ")
            : "No team"}
          {!member.covered && (
            <span className="ml-1.5 font-medium text-amber-700 dark:text-amber-400">
              · no approver
            </span>
          )}
        </span>
      </div>
      <Select
        value={(member.gradeId as string | undefined) ?? "none"}
        onValueChange={onGradeChange}
        disabled={!canEdit}
      >
        <SelectTrigger className="h-8 w-52">
          <SelectValue placeholder="No grade" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No grade</SelectItem>
          {gradeOptions.map((g) => (
            <SelectItem key={g._id} value={g._id as string}>
              {g.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex items-center gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onBlur={() => {
            if (code.trim() !== member.staffCode) {
              onStaffCodeCommit(code.trim());
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          disabled={!canEdit}
          className="h-8 w-36 font-mono uppercase"
          aria-label={`Staff code for ${member.name}`}
        />
        {member.staffCodeIsDerived && (
          <Badge className="bg-muted text-muted-foreground">Auto</Badge>
        )}
      </div>
      {canEdit && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-muted-foreground"
          onClick={onEditIndividualRate}
        >
          {hasIndividualRate ? (
            <Badge color="blue" variant="soft">
              Individual rate
            </Badge>
          ) : (
            "Individual rate"
          )}
        </Button>
      )}
      {canEdit && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-muted-foreground"
          onClick={onEditSchedule}
          aria-label={`Work schedule for ${member.name}`}
        >
          {member.workSchedule ? (
            <Badge color="blue" variant="soft">
              {weeklyHours(member.workSchedule.hours).toLocaleString("en-AU", {
                maximumFractionDigits: 1,
              })}
              h week
            </Badge>
          ) : (
            "Work schedule"
          )}
        </Button>
      )}
      <div className="ml-auto flex items-center gap-2">
        {member.isApprover && (
          <Badge color="emerald" variant="soft">
            Approver
          </Badge>
        )}
        <Switch
          checked={member.isApprover}
          onCheckedChange={onApproverChange}
          disabled={!canEdit}
          aria-label={`${member.name} can approve timesheets`}
        />
        <span className="text-xs text-muted-foreground">Can approve</span>
      </div>
      {member.isApprover && (
        <div className="flex w-full justify-end">
          <ApproverScopeControl
            memberName={member.name}
            approverTeamIds={member.approverTeamIds}
            teams={teams}
            disabled={!canEdit}
            onChange={onApproverTeamsChange}
          />
        </div>
      )}
    </div>
  );
}
